import { Button, Dialog, Field, Input, Portal, Switch } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { type ActionPublic, type ActionUpdate, ActionsService, type ApiError } from '@/client';
import useCustomToast from '@/hooks/use-custom-toast';
import { handleError } from '@/utils';

interface EditActionProps {
  action: ActionPublic;
  isOpen: boolean;
  onClose: () => void;
}

const EditAction = ({ action, isOpen, onClose }: EditActionProps) => {
  const { slug } = useParams({ strict: false });
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const showToast = useCustomToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors, isDirty },
  } = useForm<ActionUpdate>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: action,
  });

  const [archivedChecked, setArchivedChecked] = useState(action.archived);
  const [priorityChecked, setPriorityChecked] = useState(action.priority);

  const mutation = useMutation({
    mutationFn: (data: ActionUpdate) =>
      ActionsService.updateAction({
        id: action.id,
        companySlug: slug,
        requestBody: data,
      }),
    onSuccess: () => {
      showToast('Success!', 'Action updated successfully.', 'success');
      onClose();
    },
    onError: (err: ApiError) => {
      handleError(err, showToast);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['actions'] });
    },
  });

  const onSubmit: SubmitHandler<ActionUpdate> = async (data) => {
    mutation.mutate(data);
  };

  const onCancel = () => {
    reset();
    onClose();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={() => onClose()} placement="center">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content as="form" onSubmit={handleSubmit(onSubmit)}>
            <Dialog.CloseTrigger />
            <Dialog.Header>
              <Dialog.Title>{t('action.drawer.edit.title')}</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Field.Root required invalid={!!errors.name}>
                <Field.Label>
                  {t('action.columnsHeader.name.label')}
                  <Field.RequiredIndicator />
                </Field.Label>
                <Input
                  id="name"
                  {...register('name', {
                    required: t('action.columnsHeader.name.required'),
                  })}
                  placeholder={t('action.columnsHeader.name.placeholder')}
                  type="text"
                />
                <Field.ErrorText>{errors.name?.message}</Field.ErrorText>
              </Field.Root>

              <Field.Root mt={4}>
                <Switch.Root
                  name={'archived'}
                  defaultChecked={archivedChecked}
                  onCheckedChange={(e) => setArchivedChecked(e.checked)}
                >
                  <Switch.HiddenInput />
                  <Switch.Control />
                  <Switch.Label>{t('action.columnsHeader.archived')}</Switch.Label>
                </Switch.Root>
              </Field.Root>

              <Field.Root mt={4}>
                <Switch.Root
                  name={'priority'}
                  defaultChecked={priorityChecked}
                  onCheckedChange={(e) => setPriorityChecked(e.checked)}
                >
                  <Switch.HiddenInput />
                  <Switch.Control />
                  <Switch.Label>{t('action.columnsHeader.priority')}</Switch.Label>
                </Switch.Root>
              </Field.Root>

              <Field.Root required invalid={!!errors.order} mt={4}>
                <Field.Label>
                  {t('action.columnsHeader.order')}
                  <Field.RequiredIndicator />
                </Field.Label>
                <Input
                  id="order"
                  {...register('order', {
                    required: t('action.columnsHeader.order.required'),
                  })}
                  placeholder={t('action.columnsHeader.order.placeholder')}
                  type="number"
                />
                <Field.ErrorText>{errors.order?.message}</Field.ErrorText>
              </Field.Root>
            </Dialog.Body>
            <Dialog.Footer gap={3}>
              <Button onClick={onCancel} variant="outline">
                {t('ui.cancel')}
              </Button>
              <Button type="submit" loading={isSubmitting} disabled={!isDirty}>
                {t('ui.save')}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default EditAction;
